import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { useContext, useState } from 'react';

import EmailDocumentsDialog from '~components/EmailDocumentsDialog/EmailDocumentsDialog';
import OutboundEmailProgressContext from '~contexts/OutboundEmailProgressContext';
import { LoadDigestLoadFilter, LoadDigestSelection } from '~hooks/useLoadDigests/models';
import { OutboundEmailEntry } from '~hooks/useOutboundEmails/sendOutboundEmails';
import useLoadDigestOutboundEmails from '~hooks/useOutboundEmails/useLoadDigestOutboundEmails';
import useLoadDigestEmailDraftsQuery from '~queries/useLoadDigestEmailDraftsQuery';
import { useStores } from '~store';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import LoadDigestLoadFilterRadioGroup from './LoadDigestLoadFilterRadioGroup';

interface EmailLoadDigestDialogProps {
  isOpen: boolean;
  onClose: () => void;
  selection: LoadDigestSelection;
}

function EmailLoadDigestContent({
  onClose,
  selection,
}: Omit<EmailLoadDigestDialogProps, 'isOpen'>) {
  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);
  const [loadFilter, setLoadFilter] = useState(LoadDigestLoadFilter.ALL);
  const { data, drafts, isError, isLoading, refetch } = useLoadDigestEmailDraftsQuery(
    companyId,
    selection,
    loadFilter,
  );
  const { sendLoadDigestEmails } = useLoadDigestOutboundEmails();
  const { enqueueOutboundEmail } = useContext(OutboundEmailProgressContext);

  const skippedReceivedOrderCount = data?.skippedReceivedOrderCount ?? 0;
  const skippedCustomerCount = data?.skippedCustomerCount ?? 0;
  const hasSkippedSelections = skippedReceivedOrderCount + skippedCustomerCount > 0;

  const handleSend = async (emails: OutboundEmailEntry[]) => {
    const { operationId, recipientCount } = await sendLoadDigestEmails({
      companyId,
      emails,
      loadFilter,
      selection,
    });

    enqueueOutboundEmail(operationId, recipientCount);
  };

  return (
    <EmailDocumentsDialog
      drafts={drafts}
      headerContent={({ isSubmitting }) => (
        <>
          <LoadDigestLoadFilterRadioGroup
            isDisabled={isSubmitting}
            onChange={setLoadFilter}
            value={loadFilter}
          />
          {hasSkippedSelections && (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {t('dispatch.load_digest.skipped', {
                customerCount: skippedCustomerCount,
                receivedOrderCount: skippedReceivedOrderCount,
              })}
            </Typography>
          )}
        </>
      )}
      isError={isError}
      isLoading={isLoading}
      labels={{
        description: t('dispatch.load_digest.email_description'),
        noEmails: t('dispatch.load_digest.no_emails'),
        send: t('actions.send'),
        title: t('dispatch.load_digest.email_title'),
      }}
      onClose={onClose}
      onRetry={refetch}
      onSend={handleSend}
    />
  );
}

export default function EmailLoadDigestDialog({
  isOpen,
  ...contentProps
}: EmailLoadDigestDialogProps) {
  return isOpen ? <EmailLoadDigestContent {...contentProps} /> : null;
}
